import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import {
  Email as EmailIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { addDoc, collection, doc, getDoc, query, where, getDocs, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase/config';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';

const EmailVerificationBooking = ({ 
  open, 
  onClose, 
  bookingData, 
  onSuccess 
}) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState(bookingData?.clientEmail || '');
  const [verificationCode, setVerificationCode] = useState('');
  const [step, setStep] = useState('email'); // 'email', 'verification', 'payment'
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sentCode, setSentCode] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [salonSettings, setSalonSettings] = useState(null);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  // Update email when bookingData changes
  useEffect(() => {
    if (bookingData?.clientEmail) {
      setEmail(bookingData.clientEmail);
    }
  }, [bookingData?.clientEmail]);

  // Fetch salon settings and check if email is already verified when component opens
  useEffect(() => {
    if (open && bookingData?.salonId) {
      fetchSalonSettings();
      checkIfEmailVerified();
    }
  }, [open, bookingData?.salonId]);

  // Check if the email is already verified
  const checkIfEmailVerified = async () => {
    if (!email || !email.includes('@')) return;

    try {
      const verifiedClientsRef = collection(db, 'verifiedClients');
      const q = query(verifiedClientsRef, where('email', '==', email.toLowerCase()));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        console.log('✅ Email already verified, skipping verification step');
        setIsEmailVerified(true);
        // If payment is required, go to payment step, otherwise create booking directly
        if (paymentRequired && selectedService) {
          setStep('payment');
        } else {
          await createBooking();
        }
      }
    } catch (error) {
      console.error('Error checking verified email:', error);
      // Continue with normal flow if check fails
    }
  };

  // Resend timer countdown
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(timer => {
          if (timer <= 1) {
            setCanResend(true);
            return 0;
          }
          return timer - 1;
        });
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendTimer]);

  const fetchSalonSettings = async () => {
    try {
      const salonDoc = await getDoc(doc(db, 'users', bookingData.salonId));
      if (salonDoc.exists()) {
        const salonData = salonDoc.data();
        setSalonSettings(salonData);
        
        // Check if payment is required
        const prepaymentEnabled = salonData.paymentSettings?.prepaymentEnabled;
        const onlinePaymentEnabled = salonData.paymentSettings?.onlinePaymentEnabled;
        const stripeEnabled = salonData.stripeEnabled;
        
        setPaymentRequired(prepaymentEnabled && onlinePaymentEnabled && stripeEnabled);
        
        // Find the selected service details
        const service = salonData.services?.find(s => s.name === bookingData.service);
        setSelectedService(service);
      }
    } catch (error) {
      console.error('Error fetching salon settings:', error);
    }
  };

  const generateVerificationCode = () => {
    return Math.floor(100000 + Math.random() * 900000).toString();
  };

  const sendVerificationEmail = async () => {
    if (!email || !email.includes('@')) {
      setError('Veuillez entrer une adresse email valide');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const code = generateVerificationCode();
      setSentCode(code);

      // Send verification email using Firebase function
      const sendVerificationCode = httpsCallable(functions, 'sendVerificationCode');
      
      const result = await sendVerificationCode({
        email: email,
        verificationCode: code,
        bookingData: {
          service: bookingData.service,
          date: dayjs(bookingData.date).format('dddd DD MMMM YYYY à HH:mm'),
          salonName: bookingData.salonName
        }
      });
      
      // Store the pending booking with verification code
      await addDoc(collection(db, 'pendingBookings'), {
        ...bookingData,
        clientEmail: email,
        verificationCode: code,
        verified: false,
        createdAt: new Date(),
        expiresAt: new Date(Date.now() + 15 * 60 * 1000) // 15 minutes
      });

      setStep('verification');
      setError('');
      
      // Start resend timer (20 seconds)
      setResendTimer(20);
      setCanResend(false);
    } catch (error) {
      console.error('Error sending verification:', error);
      setError('Erreur lors de l\'envoi du code de vérification');
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationEmail = async () => {
    if (!canResend) return;
    
    setLoading(true);
    setError('');

    try {
      const code = generateVerificationCode();
      setSentCode(code);

      // Send verification email using Firebase function
      const sendVerificationCode = httpsCallable(functions, 'sendVerificationCode');
      const result = await sendVerificationCode({
        email: email,
        verificationCode: code,
        bookingData: {
          service: bookingData.service,
          date: dayjs(bookingData.date).format('dddd DD MMMM YYYY à HH:mm'),
          salonName: bookingData.salonName
        }
      });

      // Start resend timer again (20 seconds)
      setResendTimer(20);
      setCanResend(false);
      setError('');
    } catch (error) {
      console.error('Error resending verification:', error);
      setError('Erreur lors du renvoi du code de vérification');
    } finally {
      setLoading(false);
    }
  };

  const verifyCodeAndCreateBooking = async () => {
    if (!verificationCode) {
      setError('Veuillez entrer le code de vérification');
      return;
    }

    if (verificationCode !== sentCode) {
      setError('Code de vérification incorrect');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Save the verified email to Firebase
      await saveVerifiedClient();

      // If payment is required, go to payment step
      if (paymentRequired && selectedService) {
        setStep('payment');
        return;
      }

      // Otherwise, create booking directly
      await createBooking();
    } catch (error) {
      console.error('Error during verification process:', error);
      
      // Rediriger vers la page de confirmation avec erreur
      navigate('/booking-confirmation', {
        state: {
          success: false,
          message: 'Une erreur est survenue lors de la vérification. Veuillez réessayer ou contacter directement le salon.',
          bookingData: {
            ...bookingData,
            clientEmail: email
          },
          salonData: salonSettings
        }
      });
    } finally {
      setLoading(false);
    }
  };

  // Save verified client to Firebase
  const saveVerifiedClient = async () => {
    try {
      const verifiedClientRef = doc(db, 'verifiedClients', email.toLowerCase());
      await setDoc(verifiedClientRef, {
        email: email.toLowerCase(),
        verifiedAt: new Date(),
        lastBookingAt: new Date()
      }, { merge: true });
      
      setIsEmailVerified(true);
    } catch (error) {
      console.error('Error saving verified client:', error);
      // Don't fail the booking if this fails - continue with booking creation
      setIsEmailVerified(true);
    }
  };

  const createBooking = async (paymentIntentId = null) => {
    setLoading(true);
    setError('');

    try {
      // Check if salon has auto-confirmation enabled
      const autoConfirmBookings = salonSettings?.bookingSettings?.autoConfirmBookings ?? false;
      const bookingStatus = paymentIntentId ? 'confirmed' : (autoConfirmBookings ? 'confirmed' : 'pending');
      
      // Create the actual booking
      const bookingRef = await addDoc(collection(db, 'bookings'), {
        ...bookingData,
        clientEmail: email,
        verified: true,
        status: bookingStatus,
        paymentIntentId: paymentIntentId,
        paymentStatus: paymentIntentId ? 'paid' : 'unpaid',
        reminderSent: false,
        reviewRequestSent: false,
        createdAt: new Date()
      });

      // Send notification email to salon if enabled
      try {
        if (salonSettings?.emailSettings?.newBookingNotifications) {
          const sendNotification = httpsCallable(functions, 'sendNewBookingNotification');
          await sendNotification({
            salonEmail: salonSettings.email,
            bookingData: {
              clientName: bookingData.clientName,
              clientEmail: email,
              clientPhone: bookingData.clientPhone,
              service: bookingData.service,
              date: dayjs(bookingData.date).format('DD/MM/YYYY'),
              time: dayjs(bookingData.date).format('HH:mm'),
              paymentStatus: paymentIntentId ? 'paid' : 'unpaid',
              salonName: salonSettings.businessName || salonSettings.salonName
            }
          });
        }
      } catch (emailError) {
        console.error('Error sending booking notification:', emailError);
        // Don't fail the booking if email fails
      }

      // Send appropriate email to client based on booking status and salon settings
      try {
        if (email) {
          const sendClientEmail = httpsCallable(functions, 'sendClientBookingEmail');
          
          // Determine which email to send based on auto-confirmation setting
          const emailStatus = bookingStatus === 'confirmed' ? 'confirmed' : 'pending';
          
          await sendClientEmail({
            clientEmail: email,
            bookingStatus: emailStatus,
            bookingData: {
              clientName: bookingData.clientName,
              service: bookingData.service,
              date: dayjs(bookingData.date).format('DD/MM/YYYY'),
              time: dayjs(bookingData.date).format('HH:mm'),
              salonName: salonSettings?.businessName || bookingData.salonName,
              salonPhone: salonSettings?.phone,
              salonEmail: salonSettings?.email
            }
          });
        }
      } catch (emailError) {
        console.error('Error sending client email:', emailError);
        // Don't fail the booking if email fails
      }

      const successMessage = paymentIntentId
        ? `Réservation confirmée et paiement effectué! Le salon ${salonSettings?.businessName || bookingData.salonName} a reçu votre demande. Vous recevrez une confirmation par email.`
        : bookingStatus === 'confirmed'
        ? `Réservation confirmée ! Votre rendez-vous chez ${salonSettings?.businessName || bookingData.salonName} est confirmé. Vous recevrez une confirmation par email.`
        : `Merci ! Le salon ${salonSettings?.businessName || bookingData.salonName} a reçu votre demande de réservation. Vous serez contacté pour confirmer votre rendez-vous.`;
      
      // Rediriger vers la page de confirmation
      navigate('/booking-confirmation', {
        state: {
          success: true,
          message: successMessage,
          bookingData: {
            ...bookingData,
            clientEmail: email
          },
          salonData: salonSettings
        }
      });
    } catch (error) {
      console.error('Error creating booking:', error);
      
      // Rediriger vers la page de confirmation avec erreur
      navigate('/booking-confirmation', {
        state: {
          success: false,
          message: 'Une erreur est survenue lors de la création de votre réservation. Veuillez réessayer ou contacter directement le salon.',
          bookingData: {
            ...bookingData,
            clientEmail: email
          },
          salonData: salonSettings
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const processPayment = async () => {
    if (!selectedService) {
      setError('Service non trouvé');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create payment intent
      const createPaymentIntent = httpsCallable(functions, 'createBookingPaymentIntent');
      const result = await createPaymentIntent({
        amount: Math.round(selectedService.price * 100), // Convert to cents
        currency: 'eur',
        salonId: bookingData.salonId,
        bookingId: 'temp-' + Date.now(), // Temporary ID
        applicationFeeAmount: Math.round(selectedService.price * 100 * 0.05) // 5% commission
      });

      const { clientSecret, paymentIntentId } = result.data;

      // For demo purposes, simulate successful payment
      // In a real app, you would integrate with Stripe Elements here
      setTimeout(async () => {
        await createBooking(paymentIntentId);
      }, 2000);

    } catch (error) {
      console.error('Error processing payment:', error);
      setError('Erreur lors du traitement du paiement');
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('email');
    setEmail(bookingData?.clientEmail || '');
    setVerificationCode('');
    setError('');
    setSentCode('');
    setResendTimer(0);
    setCanResend(false);
    setPaymentRequired(false);
    setSelectedService(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <EmailIcon />
          Vérification Email
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {step === 'email' ? (
          <Box>
            <Typography variant="body1" gutterBottom>
              {bookingData?.clientEmail ?
                'Nous allons envoyer un code de vérification à votre adresse email.' :
                'Pour confirmer votre réservation, nous devons vérifier votre adresse email.'
              }
            </Typography>
            
            <Box mt={2} mb={2}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Récapitulatif de votre réservation:</strong>
              </Typography>
              <Typography variant="body2">
                • Service: {bookingData?.service}
              </Typography>
              <Typography variant="body2">
                • Date: {bookingData?.date ? dayjs(bookingData.date).format('dddd DD MMMM YYYY à HH:mm') : ''}
              </Typography>
              <Typography variant="body2">
                • Salon: {bookingData?.salonName}
              </Typography>
            </Box>

            {!bookingData?.clientEmail ? (
              <TextField
                fullWidth
                label="Adresse email *"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!error}
                helperText={error}
                sx={{ mt: 2 }}
              />
            ) : (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Email de confirmation:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {email}
                </Typography>
              </Box>
            )}
          </Box>
        ) : step === 'verification' ? (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Un code de vérification a été envoyé à <strong>{email}</strong>
            </Alert>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Entrez le code de 6 chiffres reçu par email:
            </Typography>
            
            <TextField
              fullWidth
              label="Code de vérification"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              error={!!error}
              helperText={error}
              sx={{ mt: 2 }}
            />
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Vous n'avez pas reçu le code ?
              </Typography>
              <Button
                onClick={resendVerificationEmail}
                disabled={!canResend || loading}
                size="small"
                sx={{
                  color: canResend ? '#1976d2' : 'text.disabled',
                  textTransform: 'none'
                }}
              >
                {resendTimer > 0
                  ? `Renvoyer dans ${resendTimer}s`
                  : 'Renvoyer le code'
                }
              </Button>
            </Box>
            
            {paymentRequired && selectedService && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Après vérification, vous devrez effectuer le paiement de <strong>{selectedService.price}€</strong> pour confirmer votre réservation.
              </Alert>
            )}
          </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              <CheckCircleIcon sx={{ mr: 1 }} />
              Email vérifié avec succès!
            </Alert>
            
            <Typography variant="h6" gutterBottom>
              Paiement requis
            </Typography>
            
            <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Service:</strong> {selectedService?.name}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Durée:</strong> {selectedService?.duration} minutes
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Prix:</strong> {selectedService?.price}€
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {bookingData?.date ? dayjs(bookingData.date).format('dddd DD MMMM YYYY à HH:mm') : ''}
              </Typography>
            </Box>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Le paiement sécurisé est traité par Stripe. Votre réservation sera automatiquement confirmée après le paiement.
            </Alert>
            
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            
            {loading && (
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <CircularProgress size={20} />
                <Typography variant="body2">Traitement du paiement...</Typography>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        
        {step === 'email' && (
          <Button
            onClick={sendVerificationEmail}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#f5f5dc',
              color: 'black',
              '&:hover': {
                backgroundColor: '#e8e8d0'
              }
            }}
          >
            {loading ? 'Envoi...' : 'Envoyer le code'}
          </Button>
        )}
        
        {step === 'verification' && (
          <Button
            onClick={verifyCodeAndCreateBooking}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#f5f5dc',
              color: 'black',
              '&:hover': {
                backgroundColor: '#e8e8d0'
              }
            }}
          >
            {loading ? 'Vérification...' : paymentRequired ? 'Continuer vers le paiement' : 'Confirmer la réservation'}
          </Button>
        )}
        
        {step === 'payment' && (
          <Button
            onClick={processPayment}
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: '#635bff',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5a52e8'
              }
            }}
          >
            {loading ? 'Paiement en cours...' : `Payer ${selectedService?.price}€`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default EmailVerificationBooking;